/**
 * ValidationDashboard + PublishButton Components — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import type { ValidationResult } from '../../types';

// PublishButton
export interface PublishButtonProps {
  validationStatus?: string;
  disabled?: boolean;
  isPublishing?: boolean;
  onClick?: () => void;
}

export function PublishButton({ validationStatus, disabled, isPublishing, onClick }: PublishButtonProps) {
  const isDisabled = disabled || isPublishing || validationStatus === 'blocked';
  return (
    <Button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      className="font-medium text-sm rounded-lg px-4 py-2 inline-flex items-center gap-2"
    >
      {isPublishing && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
      {isPublishing ? 'Publishing...' : 'Publish'}
    </Button>
  );
}

// ValidationDashboard
export interface ValidationDashboardProps {
  validationResult: ValidationResult | null;
  onPublish: (args?: { version_id: string }) => void;
  isPublishing?: boolean;
  validationStatus?: string;
}

export function ValidationDashboard({ validationResult, onPublish, isPublishing, validationStatus }: ValidationDashboardProps) {
  const [showDialog, setShowDialog] = useState(false);

  // Null guard — show error state
  if (!validationResult) {
    return (
      <div data-testid="validation-dashboard" role="main" className="space-y-4">
        <div role="alert" className="p-3 bg-red-50 border border-brand-error/40 rounded">
          <p className="text-sm text-brand-error">Unable to load validation results.</p>
        </div>
      </div>
    );
  }

  const blockingErrors = validationResult.blocking_errors ?? [];
  const hasBlockingErrors = blockingErrors.length > 0 || validationResult.status === 'blocked';
  const allPassed = !hasBlockingErrors && validationResult.status !== 'blocked';

  const handlePublishClick = () => {
    if (!hasBlockingErrors) {
      setShowDialog(true);
    }
  };

  const handleConfirm = () => {
    setShowDialog(false);
    onPublish({ version_id: validationResult.version_id ?? '' });
  };

  const handleCancel = () => {
    setShowDialog(false);
  };

  return (
    <div data-testid="validation-dashboard" role="main" className="space-y-4">
      {allPassed && (
        <div className="p-3 bg-green-50 border border-brand-success/40 rounded-lg">
          <p className="text-sm text-brand-success font-medium">All validations passed — ready to publish</p>
        </div>
      )}

      {hasBlockingErrors && (
        <div role="alert" aria-live="polite" className="p-3 bg-red-50 border border-brand-error/40 rounded-lg">
          <p className="text-sm font-semibold text-brand-error mb-2">Blocking validation errors:</p>
          <ul className="list-disc pl-4 space-y-1">
            {blockingErrors.map((err, i) => (
              <li key={i} className="text-sm text-brand-error">{err.condition}</li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        disabled={hasBlockingErrors || isPublishing}
        onClick={handlePublishClick}
        className={`px-4 py-2 bg-brand-success text-white rounded text-sm font-medium transition-opacity ${hasBlockingErrors ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-success/90'}`}
      >
        {isPublishing ? 'Saving...' : 'Publish'}
      </button>

      {showDialog && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm publish"
          className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
        >
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full mx-4">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Confirm Publish</h2>
            <p className="text-sm text-brand-steel mb-6">
              Are you sure you want to publish this version? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <button
                type="button"
                onClick={handleConfirm}
                className="px-4 py-2 bg-brand-crimson text-white rounded text-sm font-medium"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ValidationDashboard;
